import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { from, lastValueFrom, Observable, catchError, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import * as Sentry from "@sentry/angular";
import { environment } from "../../../environments/environment";
import { AppDataState } from "../state-stores/app-data-state.state";

/**
 * HTTP interceptor that attaches a Bearer token to outgoing HTTP requests
 * for authentication with a backend service.
 * 
 * It uses MSAL to silently acquire an access token for the configured client ID.
 * If token acquisition fails, the request proceeds without an Authorization header.
 * 
 * This interceptor supports scenarios where backend endpoints require Azure AD access tokens.
 */
@Injectable()
export class BackendRequestInterceptor implements HttpInterceptor {
  private readonly authService = inject(MsalService);
  private readonly appDataState = inject(AppDataState);

  private getPathAndQuery(urlWithParams: string): { path: string; queryString: string } {
    try {
      const parsedUrl = new URL(urlWithParams, window.location.origin);
      return {
        path: parsedUrl.pathname,
        queryString: parsedUrl.search,
      };
    } catch {
      const [path, query] = urlWithParams.split("?");
      return {
        path: path ?? urlWithParams,
        queryString: query ? `?${query}` : "",
      };
    }
  }

  private getOperationName(body: unknown): string | null {
    if (!body || typeof body !== "object") {
      return null;
    }

    const bodyRecord = body as Record<string, unknown>;
    if (typeof bodyRecord["operationName"] === "string") {
      return bodyRecord["operationName"];
    }

    return null;
  }

  private getRequestBodyForLogging(body: unknown): unknown {
    if (body === undefined) {
      return null;
    }

    try {
      return JSON.parse(JSON.stringify(body));
    } catch {
      return "[Unserializable request body]";
    }
  }

  private logRequestTrace(req: HttpRequest<any>, requestId: string, userSSOLink: string, appDataState: AppDataState): void {
    const requestBody = this.getRequestBodyForLogging(req.body);
    const operationName = this.getOperationName(req.body);
    const { path, queryString } = this.getPathAndQuery(req.urlWithParams);
    const isGraphQlRequest = path.toLowerCase().includes("graphql") || operationName !== null;



    Sentry.logger.info(Sentry.logger.fmt`[Frontend] Outgoing request. RequestId: ${requestId}`, {
        AppInstanceId: appDataState.getAppInstanceId(),
        OperatorId: appDataState.getOperatorId(),
        UserSSOLink: userSSOLink,
        Method: req.method,
        Path: path,
        QueryString: queryString,
        RequestId: requestId,
        IsGraphQL: isGraphQlRequest,
        OperationName: operationName,
        RequestBody: requestBody,
      }    
    );
  }

  /**
   * Intercepts outgoing HTTP requests to append an Authorization header if a valid token is available.
   * 
   * - Attempts to silently acquire a token from MSAL.
   * - On success: Clones the request and adds a Bearer token.
   * - On failure: Forwards the request unmodified.
   * 
   * @param req - The outgoing HTTP request
   * @param next - The next interceptor or HTTP handler in the chain
   * @returns An observable of the HTTP event stream
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip non-backend requests — Graph API and others are handled by MsalInterceptor
    if (!req.url.startsWith(environment.baseBackendUrl)) {
      return next.handle(req);
    }

    const userSSOLink = this.authService.instance.getAllAccounts()[0]?.idTokenClaims?.oid ?? "UnknownUser";
    const requestId = req.headers.get("request-id") ?? req.headers.get("x-request-id") ?? crypto.randomUUID();

    this.logRequestTrace(req, requestId, userSSOLink, this.appDataState);

    return from(this.authService.initialize()).pipe(
      switchMap(() => from(this.authService.instance.initialize())),
      switchMap(() => {
        const request = {
          scopes: [`${environment.msalConfig.auth.clientId}/.default`]
        };
        return from(lastValueFrom(this.authService.acquireTokenSilent(request))).pipe(
          catchError(() => {
            // If acquireTokenSilent fails, return an Observable that emits null
            return of(null);
          })
        );
      }),
      switchMap((tokenResponse: { accessToken?: string } | null) => {
        // Attach the signed MFA assertion token (if this session has already completed an OTP
        // challenge) so PermissionMiddleware can skip re-prompting for MFA on every request.
        const mfaToken = this.appDataState.getMfaAssertionToken();
        const mfaHeaders: Record<string, string> = {};
        if (mfaToken) {
          mfaHeaders['X-Mfa-Token'] = mfaToken;
        }

        if (tokenResponse && tokenResponse.accessToken) {
          const authorizedRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
              ...mfaHeaders
            }
          });
          return next.handle(authorizedRequest);
        } else if (mfaToken) {
          return next.handle(req.clone({ setHeaders: mfaHeaders }));
        } else {
          // If token acquisition failed, proceed without modifying the request
          return next.handle(req);
        }
      })
    );
  }
}
